// Notification types
export type NotificationSeverity = "success" | "error" | "info" | "warning";

// Notification interface
export interface NotificationOptions {
	message: string;
	severity: NotificationSeverity;
	duration?: number;
	position?:
		| "top-left"
		| "top-right"
		| "bottom-left"
		| "bottom-right"
		| "top-center"
		| "bottom-center";
	autoHide?: boolean;
}

// Global notification state management
let notificationCallbacks: Array<(notification: NotificationOptions) => void> =
	[];

// Subscribe to notifications
export const subscribeToNotifications = (
	callback: (notification: NotificationOptions) => void
) => {
	notificationCallbacks.push(callback);

	// Return unsubscribe function
	return () => {
		notificationCallbacks = notificationCallbacks.filter(
			(cb) => cb !== callback
		);
	};
};

// Show notification function
export const showNotification = (options: NotificationOptions) => {
	// Notify all subscribers
	notificationCallbacks.forEach((callback) => callback(options));
};

// Convenience functions for common notification types
export const showSuccessNotification = (
	message: string,
	options?: Partial<NotificationOptions>
) => {
	return showNotification({
		message,
		severity: "success",
		duration: 6000,
		position: "bottom-right",
		autoHide: true,
		...options,
	});
};

export const showErrorNotification = (
	message: string,
	options?: Partial<NotificationOptions>
) => {
	return showNotification({
		message,
		severity: "error",
		duration: 8000,
		position: "bottom-right",
		autoHide: true,
		...options,
	});
};

export const showInfoNotification = (
	message: string,
	options?: Partial<NotificationOptions>
) => {
	return showNotification({
		message,
		severity: "info",
		duration: 5000,
		position: "bottom-right",
		autoHide: true,
		...options,
	});
};

export const showWarningNotification = (
	message: string,
	options?: Partial<NotificationOptions>
) => {
	return showNotification({
		message,
		severity: "warning",
		duration: 7000,
		position: "bottom-right",
		autoHide: true,
		...options,
	});
};

// Clear all notifications (notify subscribers to clear)
export const clearAllNotifications = () => {
	notificationCallbacks.forEach((callback) =>
		callback({
			message: "",
			severity: "info",
			autoHide: false,
		})
	);
};
